import { useTranslation } from "@/i18n/LanguageContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UnsolvedPuzzleCard } from "./UnsolvedPuzzleCard";
import { SolvedPuzzleCard } from "./SolvedPuzzleCard";
import { CustomPuzzle, User } from "../types";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PuzzleTabsProps {
  activeTab: string;
  setActiveTab: (value: string) => void;
  unsolvedPuzzles: CustomPuzzle[];
  solvedPuzzles: CustomPuzzle[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
  onPageChange: (page: number) => void;
  user: User | undefined;
  onNavigate: (path: string) => void;
  onOpenSolveDialog: (puzzleId: number) => void;
  onOpenRatingDialog: (puzzleId: number) => void;
  onOpenAnswerDialog: (puzzleId: number) => void;
}

export function PuzzleTabs({
  activeTab,
  setActiveTab,
  unsolvedPuzzles,
  solvedPuzzles,
  pagination,
  onPageChange,
  user,
  onNavigate,
  onOpenSolveDialog,
  onOpenRatingDialog,
  onOpenAnswerDialog
}: PuzzleTabsProps) {
  const { t } = useTranslation();

  // Pagination functions
  const handlePreviousPage = () => {
    if (pagination.currentPage > 1) {
      onPageChange(pagination.currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.currentPage < pagination.totalPages) {
      onPageChange(pagination.currentPage + 1);
    }
  };

  return (
    <Tabs defaultValue="unsolved" value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="grid w-full grid-cols-2 bg-zinc-800/80 mb-6 rounded-lg p-1">
        <TabsTrigger 
          value="unsolved" 
          className="data-[state=active]:bg-zinc-700 text-xs sm:text-sm py-2 rounded-md transition-all"
        >
          {t("desafios_cacatais.unsolved_puzzles", { fallback: "Desafios Não Resolvidos" })}
          {unsolvedPuzzles.length > 0 && (
            <Badge variant="secondary" className="ml-1 md:ml-2 px-1.5 py-0">
              {unsolvedPuzzles.length}
            </Badge>
          )}
        </TabsTrigger>
        <TabsTrigger 
          value="solved" 
          className="data-[state=active]:bg-zinc-700 text-xs sm:text-sm py-2 rounded-md transition-all"
        >
          {t("desafios_cacatais.solved_puzzles", { fallback: "Desafios Resolvidos" })}
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="unsolved" className="mt-0">
        {unsolvedPuzzles.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3 lg:gap-6">
              {unsolvedPuzzles.map((puzzle) => (
                <UnsolvedPuzzleCard
                  key={puzzle.id}
                  puzzle={puzzle}
                  user={user}
                  onNavigate={onNavigate}
                  onOpenSolveDialog={onOpenSolveDialog}
                  onOpenRatingDialog={onOpenRatingDialog}
                />
              ))}
            </div>
            {/* Pagination for unsolved puzzles */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <Button
                  variant="outline"
                  className="p-2 h-9 w-9"
                  onClick={handlePreviousPage}
                  disabled={pagination.currentPage === 1}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <span className="text-sm text-zinc-400">
                  {t('other.pagination_t.page_of_total', {
                    current: pagination.currentPage,
                    total: pagination.totalPages
                  })}
                </span>
                <Button
                  variant="outline"
                  className="p-2 h-9 w-9"
                  onClick={handleNextPage}
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>{t("desafios_cacatais.no_unsolved_puzzles", { fallback: "No unsolved puzzles" })}</CardTitle>
              <CardDescription>{t("desafios_cacatais.check_solved_tab", { fallback: "Check the solved tab to see completed challenges" })}</CardDescription>
            </CardHeader>
          </Card>
        )}
      </TabsContent>
      
      <TabsContent value="solved" className="mt-0">
        {solvedPuzzles.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3 lg:gap-6">
              {solvedPuzzles.map((puzzle) => (
                <SolvedPuzzleCard
                  key={puzzle.id}
                  puzzle={puzzle}
                  user={user}
                  onNavigate={onNavigate}
                  onOpenRatingDialog={onOpenRatingDialog}
                  onOpenAnswerDialog={onOpenAnswerDialog}
                />
              ))}
            </div>
            {/* Pagination for solved puzzles */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <Button
                  variant="outline"
                  className="p-2 h-9 w-9"
                  onClick={handlePreviousPage}
                  disabled={pagination.currentPage === 1}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <span className="text-sm text-zinc-400">
                  {t('other.pagination_t.page_of_total', {
                    fallback: 'Page %{current} of %{total}',
                    current: pagination.currentPage,
                    total: pagination.totalPages
                  })}
                </span>
                <Button
                  variant="outline"
                  className="p-2 h-9 w-9"
                  onClick={handleNextPage}
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>{t("desafios_cacatais.no_solved_puzzles", { fallback: "No solved puzzles yet" })}</CardTitle>
              <CardDescription>{t("desafios_cacatais.try_solve", { fallback: "Try solving a puzzle from the unsolved tab" })}</CardDescription>
            </CardHeader>
          </Card>
        )}
      </TabsContent>
    </Tabs>
  );
}
